import { makeClientId, type IScriptTemplate } from "@shadowcue/ui";
import { blankSection, blankQuestion } from "./script_builder";

// Shared between the create (new.tsx) and edit (edit.tsx) screens so both
// validate and initialize identically.

// Builds a starting section with 2 questions instead of blankSection()'s
// default 1, reusing blankSection()/blankQuestion() so every clientId is
// generated the same way (and stays unique) as the rest of the builder.
const startingSection = () => {
  const section = blankSection();
  return { ...section, scriptTemplateQuestions: [...section.scriptTemplateQuestions, blankQuestion()] };
};

export const blankScriptTemplate: IScriptTemplate = {
  name: "",
  description: "",
  notes: "",
  scriptTemplateSections: [startingSection(), startingSection()],
};

export const validate = (values: IScriptTemplate): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!values.name.trim()) {
    errors["name"] = "Script name is required.";
  }

  values.scriptTemplateSections.forEach((section, i) => {
    if (!section.name.trim()) {
      errors[`section-${i}-name`] = "Section name is required.";
    }
    section.scriptTemplateQuestions.forEach((question, j) => {
      if (!question.body.trim()) {
        errors[`question-${i}-${j}-body`] = "Question is required.";
      }
    });
  });

  return errors;
};

export const pluralize = (count: number, noun: string): string =>
  `${count} ${count === 1 ? noun : `${noun}s`}`;

export const scrollToField = (fieldKey: string): void => {
  requestAnimationFrame(() => {
    document
      .querySelector(`[data-field-key="${fieldKey}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
};

/**
 * Templates fetched from the API arrive without `clientId`s (the server
 * doesn't know about them), but the drag-and-drop builder in
 * script_builder.tsx requires every section and question to have one so
 * dnd-kit can track items across reorders. Returns a copy with any missing
 * `clientId`s backfilled, preserving existing ones.
 */
export const withClientIds = (template: IScriptTemplate): IScriptTemplate => ({
  ...template,
  scriptTemplateSections: template.scriptTemplateSections.map((section) => ({
    ...section,
    clientId: section.clientId ?? makeClientId(),
    scriptTemplateQuestions: section.scriptTemplateQuestions.map((question) => ({
      ...question,
      clientId: question.clientId ?? makeClientId(),
    })),
  })),
});

export const scriptTemplateToPayload = (scriptTemplate: IScriptTemplate) => ({
  script_template: {
    name: scriptTemplate.name,
    description: scriptTemplate.description,
    notes: scriptTemplate.notes,
    scriptTemplateSectionsAttributes: scriptTemplate.scriptTemplateSections.map(
      (section) => ({
        name: section.name,
        scriptTemplateQuestionsAttributes:
          section.scriptTemplateQuestions.map((question) => ({
            body: question.body,
            prospectiveResponse: question.prospectiveResponse,
          })),
      }),
    ),
  },
});
